import { TruncatedTextContent } from '@/features/truncated-text-content';
import { IProductCart } from '@/shared/api/getProduct/types';
import { useExpandedArray } from '@/shared/hooks/useExpandedArray';
import { Button } from '@/shared/ui/Button';
import { CountBadge } from '@/shared/ui/CountBadge';
import { HtmlContent } from '@/shared/ui/HtmlContent';
import { Link } from '@/shared/ui/Link';
import { Properties } from '@/shared/ui/Properties';
import { twMergeExt } from '@/shared/utils/twMergeExt';
import { cva } from 'class-variance-authority';
import { FC, useMemo } from 'react';
import { PREFETCH } from '@/shared/constants';

interface Props {
  className?: string;
  data: IProductCart;
}

export const DescriptionTab: FC<Props> = ({ className, data }) => {
  const {
    visibleItems: visibleAppointments,
    moreCount: moreAppointmentsCount,
    onExpand: onAppointmentsExpand,
  } = useExpandedArray(data.appointment || [], 3);

  const properties = useMemo(() => {
    const obj: Record<string, string> = {};

    if (data.product_type) obj['Тип продукта'] = data.product_type.join(', ');
    if (data.frequency_usage) obj['Частота применения'] = data.frequency_usage.join(', ');
    if (data.usage) obj['Область применения'] = data.usage.join(', ');

    if (data.hair_type) {
      obj['Тип волос'] = data.hair_type;
    }

    if (data.skin_condition) {
      obj['Состояние кожи'] = data.skin_condition;
    }

    if (data.age) {
      obj['Возраст'] = data.age.join(', ');
    }

    if (data.texture) {
      obj['Текстура'] = data.texture;
    }

    return Object.keys(obj).length ? obj : null;
  }, [data]);

  return (
    <div className={twMergeExt(cvaRoot(), className)}>
      <div className={'flex flex-col gap-6.4 flex-shrink-0 flex-grow-0 md:basis-[45.6rem]'}>
        <h3 className={'text-h3 font-secondary max-w-[25.1rem]'}>{data.original_name || data.name}</h3>

        {data.appointment?.length ? (
          <div className={'flex flex-col gap-2.4'}>
            <div className={'text-bodyM text-cBlack48'}>Назначение</div>
            <div className={'flex flex-wrap gap-0.8'}>
              {visibleAppointments.map((i, index) => (
                <Link
                  key={index}
                  className={'px-1.6 text-bodyM'}
                  size={'small'}
                  color={'mediumGreyOnWhite'}
                  hoverEffect={'highlight'}
                  href={`/filters/${i.code}`}
                  prefetch={PREFETCH.FILTERS}
                >
                  {i.name}
                </Link>
              ))}
              {moreAppointmentsCount > 0 ? (
                <Button
                  className={'relative pl-1.6 pr-2 border border-cBlack32 text-bodyM'}
                  size={'small'}
                  color={'white'}
                  onClick={onAppointmentsExpand}
                  hoverEffect={'highlight'}
                  badgeSlot={<CountBadge className={'top-1.1 right-0.8'}>{moreAppointmentsCount}</CountBadge>}
                >
                  Ещё
                </Button>
              ) : null}
            </div>
          </div>
        ) : null}

        {properties ? (
          <div className={'flex flex-col gap-2.4'}>
            <div className={'text-bodyM text-cBlack48'}>Подробные характеристики</div>
            <Properties className={'text-bodyM bg-cWhite'} properties={properties} />
            <div className={'flex'}></div>
          </div>
        ) : null}
      </div>
      <TruncatedTextContent className={'flex-1'} lines={10} onExpandTrigger={() => onAppointmentsExpand()}>
        <HtmlContent className={'text-bodyL text-cBlack80'}>{data.description}</HtmlContent>
      </TruncatedTextContent>
    </div>
  );
};

const cvaRoot = cva(['DescriptionTab-cvaRoot', ' flex flex-col md:gap-[15.9rem] md:flex-row gap-[3rem] px-2 md:px-0']);
